/**
 * Configuration for analytics event debouncing and deduplication.
 *
 * This configuration defines how different analytics events should be
 * debounced and deduplicated to prevent spam and ensure accurate tracking.
 */

import { EVENT_NAMES } from '../constants/event-names.js';

export interface AnalyticsDebounceConfig {
  /** Debounce delay in milliseconds */
  debounce: number;
  /** Deduplication window in milliseconds */
  dedupe: number;
  /** Whether this event type should be debounced */
  enabled: boolean;
}

export type AnalyticsDebounceConfigMap = Record<string, AnalyticsDebounceConfig>;

/**
 * Default debounce configuration for events not explicitly configured
 */
export const DEFAULT_DEBOUNCE_CONFIG: AnalyticsDebounceConfig = {
  debounce: 300,
  dedupe: 500,
  enabled: true
};

/**
 * High-frequency events that need shorter debounce times (100ms)
 * These are typically rapid user interactions like movement controls
 */
const HIGH_FREQUENCY_CONFIG: AnalyticsDebounceConfig = {
  debounce: 100,
  dedupe: 200,
  enabled: true
};

/**
 * Medium-frequency events with standard debounce (300ms)
 * These are typical UI interactions
 */
const MEDIUM_FREQUENCY_CONFIG: AnalyticsDebounceConfig = {
  debounce: 300,
  dedupe: 500,
  enabled: true
};

/**
 * Low-frequency events with longer debounce (1000ms)
 * These are critical actions that should have longer protection
 */
const LOW_FREQUENCY_CONFIG: AnalyticsDebounceConfig = {
  debounce: 1000,
  dedupe: 2000,
  enabled: true
};

/**
 * Events that should not be debounced
 * These are typically one-time events or events where timing is critical
 */
const NO_DEBOUNCE_CONFIG: AnalyticsDebounceConfig = {
  debounce: 0,
  dedupe: 0,
  enabled: false
};

/**
 * Complete debounce configuration for all analytics events
 */
export const ANALYTICS_DEBOUNCE_CONFIG: AnalyticsDebounceConfigMap = {
  // High-frequency movement and control events
  [EVENT_NAMES.CLICK_TO_CENTER_SHOT]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_QUICK_SHOTS]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_QUICK_SHOT]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_CURSOR_TOOL]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_CENTER_TOOL]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_FAST_FRAME]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_CINE_CENTER]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_CINE_FRAME]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_AUTO_TRACKING]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.CONFIRM_FAST_FRAME_SHOT]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.CONFIRM_CINE_FRAME_SHOT]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.MOVE_PAN_TILT_JOYSTICK]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.ZOOM_OUT_JOYSTICK]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.TOGGLE_PTZ_GRID_LINES]: HIGH_FREQUENCY_CONFIG,

  // Auto-tracking events (high frequency during tracking)
  [EVENT_NAMES.AUTO_TRACKING_START]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.AUTO_TRACKING_STOP]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.AUTO_TRACKING_SELECT_SUBJECT]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.AUTO_TRACKING_LOSE_SUBJECT]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.AUTO_TRACKING_STARTED]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.AUTO_TRACKING_STOPPED]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.AUTO_TRACKING_SUBJECT_ACQUIRED]: HIGH_FREQUENCY_CONFIG,
  [EVENT_NAMES.AUTO_TRACKING_SUBJECT_LOST]: HIGH_FREQUENCY_CONFIG,

  // Medium-frequency UI interactions
  [EVENT_NAMES.CLICK_NOTIFICATIONS_TRAY]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_FOCUS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_EXPOSURE_SETTINGS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_FOCUS_SETTINGS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_GENERAL_SETTINGS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_NETWORK_SETTINGS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_WHITE_BALANCE_SETTINGS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_FEATURE_SETTINGS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_IMAGE_SETTING_PRESETS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.ADVANCED_SETTINGS_OPENED]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.ADVANCED_SETTINGS_CLOSED]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.ADVANCED_SETTINGS_EXPORTED]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.EDIT_ADVANCED_VIDEO_SETTINGS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.EDIT_ADVANCED_CONTROL_SETTINGS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.EDIT_CONTROL_SETTINGS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.EDIT_SPEED_SETTINGS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.RESET_ALL_IMAGE_SETTINGS]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.USER_OPEN_BRIDGE_TRAY]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.USER_OPEN_MONITOR_TRAY]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.USER_OPEN_ELECTRON_TRAY]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.PRESS_SHARE_BUTTON]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.TOGGLE_CLOUD_MODE]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.TOGGLE_LOCAL_MODE]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.LOCK_LOCAL_MODE]: MEDIUM_FREQUENCY_CONFIG,

  // Device and source selection (medium frequency)
  [EVENT_NAMES.DEVICE_CLICK_DISCOVERED]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_CLICK_MANUAL_ADD]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_CONTROL_TYPE]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_VIEW_TYPE]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_PERMISSION_TYPE]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_DISCOVERY_SOURCE]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_LOCAL_SOURCE]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_NDI_SOURCE]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_ONVIF_SOURCE]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_RTSP_SOURCE]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_NO_CONTROL]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_SELECT_BRAND]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_SELECT_MODEL]: MEDIUM_FREQUENCY_CONFIG,
  // Historical classification. Current senders emit DISCOVERY_DEVICE_FOUND /
  // DISCOVERY_EMPTY only. Keeping the retired keys maps older captured names
  // onto the same medium-frequency class rather than the default bucket.
  [EVENT_NAMES.DEVICE_DISCOVERY_DEVICE_FOUND]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_DISCOVERY_NO_DEVICES_FOUND]: MEDIUM_FREQUENCY_CONFIG,
  // Canonical discovery names keep the same cadence the retired aliases used.
  [EVENT_NAMES.DISCOVERY_DEVICE_FOUND]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.DISCOVERY_EMPTY]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.SKIP_MODEL_ENTRY]: MEDIUM_FREQUENCY_CONFIG,

  // Audio and video settings
  [EVENT_NAMES.SET_AUDIO_DELAY]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.CHANGE_AUDIO_REFERENCE_SOURCE]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.CHANGE_FOCUS_SPEED]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.SET_SOURCE_TO_SLEEP]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.WAKE_UP_SOURCE]: MEDIUM_FREQUENCY_CONFIG,

  // Low-frequency critical actions
  [EVENT_NAMES.ADD_AUDIO_SOURCE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.ADD_POSITION_PRESET]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.RECALL_POSITION_PRESET]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.ADD_PRESET_GROUP]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.ADD_GAMEPAD_CONTROLLER]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.ADD_PAYMENT_METHOD]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.CALIBRATE_SOURCE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.CANCEL_CALIBRATION]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.CHANGE_PASSWORD]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.CHANGE_HOTKEY]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SET_NEW_HOTKEY]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SAVE_HOTKEY_CHANGES]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_ADD]: LOW_FREQUENCY_CONFIG,
  // Historical compatibility metadata. The shared sender no longer emits
  // successfully_add_camera, but code that classifies an older captured name
  // still receives the same low-frequency policy as device_add.
  //
  // BEHAVIOURALLY INERT TODAY, and deliberately so — do not read this as active
  // protection. The device_add emit path uses plain analytics/track.ts, which
  // never calls getDebounceConfig, and successfully_add_camera has no sender.
  // The only production consumer of
  // the debounce/dedup machinery in the frontend is
  // control/components/interaction/center/wiring.ts via useAnalyticsTrack. So a
  // double-invoke of the add-camera commit path is NOT deduped either before or
  // after this entry.
  //
  // Keeping the entry does not restore the sender. It lets historical replay
  // and inspection code classify the old name without falling through to a
  // different default. Locked by debounce-config.test.ts.
  [EVENT_NAMES.SUCCESSFULLY_ADD_CAMERA]: LOW_FREQUENCY_CONFIG,

  // ENG-4323: the add-camera failure terminal. LOW_FREQUENCY matches the other
  // *_FAILED events (bridge_update_failed / app_boot_failed / permissions_grant_failed)
  // — a real, low-rate failure that must not be dropped by aggressive debouncing.
  [EVENT_NAMES.DEVICE_ADD_FAILED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_DELETE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_AUTHENTICATE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DELETE_POSITION_PRESET]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DELETE_IMAGE_SETTING_PRESET]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SAVE_IMAGE_SETTING_PRESET]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.CREATE_STUDIO]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DELETE_STUDIO]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.UPDATE_STUDIO_NAME]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.CREATE_NEW_ORGANIZATION]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DELETE_ORGANIZATION]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.UPDATE_ORGANIZATION_INFO]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.ORG_CREATE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.ORG_DELETE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.ORG_UPLOAD_PHOTO]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.UPLOAD_ORG_PROFILE_PICTURE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.USER_DELETE_ACCOUNT]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.USER_UPDATE_EMAIL]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.USER_UPDATE_NAME]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.USER_UPLOAD_PROFILE_PHOTO]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.ENABLE_2FA]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DISABLE_2FA]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_MOVE_DIRECT]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_MOVE_START]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_MOVE_END]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_RESTORE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_SHADOWED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.BRIDGE_UPDATE_STARTED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.BRIDGE_UPDATE_COMPLETED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.BRIDGE_UPDATE_FAILED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.LOGIN_SCREEN_LOADED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.LOGIN_CONTINUE_WITH_EMAIL_CLICKED]: LOW_FREQUENCY_CONFIG,
  // ENG-3451: same frequency class as the email sibling — both are
  // once-per-attempt sign-in starts and must debounce identically.
  [EVENT_NAMES.LOGIN_CONTINUE_WITH_GOOGLE_CLICKED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.LOGIN_CREATE_ACCOUNT_CLICKED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.PREVIOUSLY_AUTHENTICATED_USER_REACHED_LOGIN]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.LOGIN_SUCCEEDED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.APP_BOOT_STARTED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.APP_BOOT_COMPLETED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.APP_BOOT_FAILED]: LOW_FREQUENCY_CONFIG,
  // The retired APP_BOOT_* names retain their historical frequency class above. Current
  // senders emit only these canonical names. STUDIO_LOAD_SUCCEEDED and its retired
  // predecessor are both absent, so historical classification remains unchanged.
  [EVENT_NAMES.BOOT_STARTED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.BOOT_SUCCEEDED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.BOOT_FAILED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.PERMISSIONS_MODAL_OPENED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.PERMISSIONS_MODAL_CLOSED]: LOW_FREQUENCY_CONFIG,
  // Historical compatibility: no current sender emits this name.
  [EVENT_NAMES.PERMISSIONS_MODAL_ERROR]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.PERMISSIONS_GRANT_FAILED]: LOW_FREQUENCY_CONFIG,
  // ENG-4307: once-per-flow terminal success — no debounce, matching the other terminal
  // success events (device_add, calibration_completed).
  [EVENT_NAMES.PERMISSIONS_GRANT_SUCCEEDED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.PERMISSIONS_MODAL_CONTINUE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.PERMISSIONS_MODAL_SKIP]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.PERMISSIONS_MODAL_PERMISSION_ACTION]: MEDIUM_FREQUENCY_CONFIG,

  // Billing and subscription events
  [EVENT_NAMES.CLICK_UPGRADE_PLAN]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.CLICK_DOWNLOAD_INVOICE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_STANDARD_PLAN]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_PRO_PLAN]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SELECT_ANNUAL_STANDARD_PLAN]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DOWNGRADE_PLAN]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SUCCESSFULLY_SUBSCRIBE_PAID_PLAN]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SUCCESSFULLY_SUBSCRIBE_ANNUAL_PLAN]: LOW_FREQUENCY_CONFIG,

  // Collaboration events
  [EVENT_NAMES.COLLAB_ACCEPT_INVITE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.COLLAB_CREATE_LINK]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.COLLAB_INVITE_USER]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.COLLAB_OPEN_INVITE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.COLLAB_REMOVE_USER]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.RESEND_INVITE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SHARE_EXPIRING_LINK]: LOW_FREQUENCY_CONFIG,

  // Bridge and device management
  [EVENT_NAMES.USER_LINK_BRIDGE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.USER_UNLINK_BRIDGE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.RENAME_BRIDGE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.RESTART_BRIDGE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.OVERRIDES_BRIDGE]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SENDS_BRIDGE_LINK_REQUEST]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.PRESS_ADD_IP_CONTROLLER]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SUCCESSFULLY_ADD_IP_CONTROLLER]: LOW_FREQUENCY_CONFIG,

  // Video configuration confirmations
  [EVENT_NAMES.CONFIRM_LOCAL_VIDEO_SETTINGS]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.CONFIRM_NDI_VIDEO_SETTINGS]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.CONFIRM_RTSP_VIDEO_SETTINGS]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_CONTINUE_FROM_BRAND]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_CONTINUE_FROM_CONTROL]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_CONTINUE_FROM_VIDEO]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_CONTINUE_TO_VIDEO]: LOW_FREQUENCY_CONFIG,

  // Success events (should not be debounced as they indicate completion)
  [EVENT_NAMES.SUCCESSFULLY_ADD_AUDIO_SOURCE]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.SUCCESSFULLY_CALIBRATE_SOURCE]: NO_DEBOUNCE_CONFIG,

  // Terminal calibration lifecycle event (ENG-4026). Fires once per real
  // RUNNING->terminal transition, so no debounce — mirrors FAILED_CALIBRATION.
  [EVENT_NAMES.CALIBRATION_COMPLETED]: NO_DEBOUNCE_CONFIG,

  // Terminal stream-recovery outcomes (ENG-4321). The stateful controller
  // already guarantees at-most-once per bounded window, so debounce must NOT
  // additionally collapse a genuine new window's terminal event — no debounce.
  [EVENT_NAMES.STREAM_RECOVERY_SUCCEEDED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.STREAM_RECOVERY_FAILED]: NO_DEBOUNCE_CONFIG,

  // Error and failure events (should not be debounced)
  [EVENT_NAMES.FAILED_CALIBRATION]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.DEVICE_AUTH_INVALID]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.BRIDGE_GOES_OFFLINE]: NO_DEBOUNCE_CONFIG,
  // Keep the canonical outage event immediate at the real watchdog boundary.
  // The retired literal above remains cataloged but has no active sender.
  [EVENT_NAMES.BRIDGE_CONNECTION_UNAVAILABLE]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.OAUTH_ERROR]: NO_DEBOUNCE_CONFIG,
  // ENG-4348 stage 3: canonical twin of OAUTH_ERROR — must carry the identical
  // (no-)debounce policy or the dual-write parity check would diverge.
  [EVENT_NAMES.AUTH_LOGIN_FAILED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.UNIVERSAL_LOGIN_PAGE_LOADED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.UNIVERSAL_SIGNUP_PAGE_LOADED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.UNIVERSAL_LOGIN_SUBMITTED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.UNIVERSAL_SIGNUP_SUBMITTED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.UNIVERSAL_SWITCH_TO_LOGIN_CLICKED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.UNIVERSAL_SWITCH_TO_SIGNUP_CLICKED]: NO_DEBOUNCE_CONFIG,

  // Time-based events (should not be debounced)
  [EVENT_NAMES.TIME_SPENT_IN_TOOL]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.START_RECORDING]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.DEVICE_MEDIA_QUALITY_PING]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.DEVICE_MEDIA_CONNECT_SUCCESS]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_MEDIA_CONNECT_ERROR]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_MEDIA_STREAMING_SUCCESS]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_MEDIA_STREAMING_ERROR]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_CONTROLS_CONNECT_SUCCESS]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_CONTROLS_CONNECT_ERROR]: LOW_FREQUENCY_CONFIG,
  // Canonical media/controls names (ENG-4313) mirror their legacy aliases' config so the
  // map stays consistent if a future call site adopts the debounced track path. These
  // entries are DORMANT today: the media/controls emit sites use the raw useAnalytics()
  // .track path, not the debounced path, so no config here is consulted at runtime.
  [EVENT_NAMES.MEDIA_QUALITY_PING]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.MEDIA_CONNECT_SUCCEEDED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.MEDIA_CONNECT_FAILED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.MEDIA_STREAM_SUCCEEDED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.MEDIA_STREAM_FAILED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.CONTROLS_CONNECT_SUCCEEDED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.CONTROLS_CONNECT_FAILED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_SETTINGS_CHANGE]: NO_DEBOUNCE_CONFIG,

  // Download and external events
  [EVENT_NAMES.DOWNLOAD_APP]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.USER_DOWNLOAD_ELECTRON_APP]: MEDIUM_FREQUENCY_CONFIG,
  // ENG-4347: the canonical twin must share the legacy event's debounce config —
  // divergent windows would skew the old-vs-new parity counts during dual-write.
  [EVENT_NAMES.DOWNLOAD_APP_STARTED]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.ENTER_ONVIF_PASSWORD]: MEDIUM_FREQUENCY_CONFIG,

  // Multi-Add Phase 2 (W1). Every Multi-Add funnel event is emitted with
  // NO_DEBOUNCE. The debounce map is keyed by event NAME only
  // (getDebouncedFunction in utils/debounced-track.ts), so N same-name emits
  // inside one window collapse via lodash trailing-edge into a SINGLE emission
  // carrying only the LAST payload. For per-device events (device_added /
  // needs_attention) and per-payload events (discovery_selected / chip_clicked /
  // subtab / batch_cancelled) that is silent data loss on exactly the large-batch
  // path this funnel measures, so each routes through trackImmediate (one emit per
  // call). Batch-level events (started / completed / partial_success) are one-shot
  // and carry the funnel-keyed `batchId`. (NO_DEBOUNCE also turns dedup off — fine
  // here: each device/payload is emitted exactly once, so there are no true
  // duplicates to catch.) Modal open/cancel and single-add baselines stay LOW
  // (user-initiated, never spam). Per `reference-rules-in-comments` (ai.knowledge/global).
  [EVENT_NAMES.MULTI_ADD_STARTED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.MULTI_ADD_COMPLETED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.MULTI_ADD_PARTIAL_SUCCESS]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.MULTI_ADD_DISCOVERY_SELECTED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.MULTI_ADD_DEVICE_ADDED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.MULTI_ADD_DEVICE_NEEDS_ATTENTION]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.MULTI_ADD_BATCH_CANCELLED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.MULTI_ADD_CHIP_CLICKED]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.MULTI_ADD_SUBTAB_NEEDS_ATTENTION_SHOWN]: NO_DEBOUNCE_CONFIG,
  [EVENT_NAMES.MULTI_ADD_NOTIFICATION_CLICKED]: MEDIUM_FREQUENCY_CONFIG,
  [EVENT_NAMES.DEVICE_CONFIGURATION_COMPLETED_FROM_NOTIFICATION]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SINGLE_ADD_STARTED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.SINGLE_ADD_COMPLETED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.ADD_SOURCE_MODAL_OPENED]: LOW_FREQUENCY_CONFIG,
  [EVENT_NAMES.ADD_SOURCE_MODAL_CANCELLED]: LOW_FREQUENCY_CONFIG
};

/**
 * Per-device dedupe configuration (ENG-3960 §1.2).
 *
 * device_settings_change collapses to at most ONE event per command_key per device
 * per 5s window. The dedupe state machine itself lives in
 * domains/settings-lifecycle.ts (keyed `${device_id}:${command_key}`); this constant
 * is the single source of truth for the window so the value can never drift between
 * config and implementation.
 *
 * Per rule: think-in-systems — keying is PER DEVICE (per command_key / per device id),
 * never global, so two devices changing the same setting both emit.
 */
export const DEVICE_SETTINGS_CHANGE_DEDUPE = {
  /** One event per (device_id, command_key) per this window. */
  windowMs: 5_000,
  /** Documents the composite dedupe key shape used by the settings-lifecycle tracker. */
  keyTemplate: '${device_id}:${command_key}'
} as const;

/**
 * Per-device cadence for media_quality_ping (ENG-3960 §1.2).
 *
 * After media_stream_succeeded, emit quality pings frequently during the first 30s (the
 * window where connection problems surface), then throttle to once every 5 minutes
 * for steady-state telemetry. Keyed PER DEVICE so each stream is cadenced
 * independently.
 */
export interface QualityPingCadenceConfig {
  /** Length of the post-streaming_success burst window. */
  initialWindowMs: number;
  /** Minimum gap between pings inside the burst window (0 = emit every sample). */
  initialMinIntervalMs: number;
  /** Minimum gap between pings after the burst window. */
  steadyIntervalMs: number;
}

export const DEVICE_MEDIA_QUALITY_PING_CADENCE: QualityPingCadenceConfig = {
  initialWindowMs: 30_000,
  initialMinIntervalMs: 0,
  steadyIntervalMs: 300_000
};

/**
 * Pure decision helper for the quality-ping cadence. Given when a device's stream
 * started, the last time we emitted a ping for THAT device, and now, decide whether
 * to emit. Stateless + per-device by construction (the caller owns the per-device
 * `lastPingMs` map), so it is trivially testable.
 */
export const shouldEmitQualityPing = (
  streamStartMs: number,
  lastPingMs: number | undefined,
  nowMs: number,
  config: QualityPingCadenceConfig = DEVICE_MEDIA_QUALITY_PING_CADENCE
): boolean => {
  if (lastPingMs === undefined) {
    return true;
  }

  const inInitialWindow = nowMs - streamStartMs <= config.initialWindowMs;
  const minInterval = inInitialWindow ? config.initialMinIntervalMs : config.steadyIntervalMs;

  return nowMs - lastPingMs >= minInterval;
};

/**
 * Get debounce configuration for a specific event
 */
export function getDebounceConfig(eventName: string): AnalyticsDebounceConfig {
  return ANALYTICS_DEBOUNCE_CONFIG[eventName] || DEFAULT_DEBOUNCE_CONFIG;
}

/**
 * Check if an event should be debounced
 */
export function shouldDebounceEvent(eventName: string): boolean {
  const config = getDebounceConfig(eventName);
  return config.enabled;
}

/**
 * Get all configured event names
 */
export function getConfiguredEvents(): string[] {
  return Object.keys(ANALYTICS_DEBOUNCE_CONFIG);
}

/**
 * Get events by frequency category
 */
export function getEventsByFrequency(frequency: 'high' | 'medium' | 'low' | 'none'): string[] {
  const targetConfig = {
    high: HIGH_FREQUENCY_CONFIG,
    medium: MEDIUM_FREQUENCY_CONFIG,
    low: LOW_FREQUENCY_CONFIG,
    none: NO_DEBOUNCE_CONFIG
  }[frequency];

  return Object.entries(ANALYTICS_DEBOUNCE_CONFIG)
    .filter(
      ([, config]) =>
        config.debounce === targetConfig.debounce &&
        config.dedupe === targetConfig.dedupe &&
        config.enabled === targetConfig.enabled
    )
    .map(([eventName]) => eventName);
}
